import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  ExternalLink,
  FileSearch,
  Filter,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";
import { apiService } from "../../services/api";
import { useAuth } from "../Auth";

const SOURCE_OPTIONS = [
  { value: "journal", label: "Journal" },
  { value: "expense", label: "Factures" },
  { value: "objective", label: "Objectifs" },
  { value: "inventory", label: "Inventaire" },
  { value: "drive", label: "Drive" },
  { value: "guttenberg", label: "Guttenberg" },
];
const INDEX_SOURCE_OPTIONS = SOURCE_OPTIONS.filter((source) => source.value !== "guttenberg");

const STATUS_DONE = new Set(["completed", "failed"]);

const KnowledgePage = () => {
  const { user } = useAuth();
  const isSuperAdmin = Number(user?.employee_id ?? user?.id) === 1;
  const isAdmin = Boolean(user?.is_admin) || isSuperAdmin;

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [projectSearch, setProjectSearch] = useState("");
  const [projectStatuses, setProjectStatuses] = useState({});
  const [status, setStatus] = useState(null);
  const [job, setJob] = useState(null);
  const [query, setQuery] = useState("");
  const [sourceTypes, setSourceTypes] = useState([]);
  const [results, setResults] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingProjectStatuses, setLoadingProjectStatuses] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [indexing, setIndexing] = useState(false);
  const [syncingDrive, setSyncingDrive] = useState(false);
  const [searching, setSearching] = useState(false);
  const [loadingSource, setLoadingSource] = useState(false);
  const [error, setError] = useState("");
  const [driveSyncMessage, setDriveSyncMessage] = useState("");

  const selectedProject = useMemo(
    () => projects.find((project) => String(project.id) === String(projectId)),
    [projects, projectId]
  );

  const selectedProjectSummary = projectId ? projectStatuses[String(projectId)] : null;
  const displayStatus = projectId ? status || selectedProjectSummary : null;

  const filteredProjects = useMemo(() => {
    const search = normalizeProjectSearch(projectSearch);
    if (!search) return projects;
    return projects.filter((project) =>
      normalizeProjectSearch(`${projectLabel(projects, project.id)} ${project.id} ${project.status || ""}`).includes(search)
    );
  }, [projects, projectSearch]);

  const vectorizedProjectCount = useMemo(
    () =>
      projects.reduce(
        (count, project) => count + (isProjectVectorized(projectStatuses[String(project.id)]) ? 1 : 0),
        0
      ),
    [projects, projectStatuses]
  );

  useEffect(() => {
    let active = true;
    setLoadingProjects(true);
    apiService
      .getProjectOptions()
      .then((data) => {
        if (!active) return;
        const list = Array.isArray(data) ? data : [];
        setProjects(list);
        loadProjectStatuses(list);
      })
      .catch((err) => active && setError(err.message || "Impossible de charger les projets."))
      .finally(() => active && setLoadingProjects(false));
    return () => {
      active = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setDriveSyncMessage("");
    setStatus(null);
    setJob(null);
    if (!projectId) {
      return;
    }
    refreshStatus(projectId);
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!job?.id || STATUS_DONE.has(job.status)) return undefined;
    const interval = setInterval(async () => {
      try {
        const nextJob = await apiService.getKnowledgeJob(job.id);
        setJob(nextJob);
        if (STATUS_DONE.has(nextJob.status) && projectId) {
          refreshStatus(projectId);
          loadProjectStatuses(projects);
          setIndexing(false);
        }
      } catch (err) {
        setError(err.message || "Impossible de suivre le job d'indexation.");
        setIndexing(false);
      }
    }, 2500);
    return () => clearInterval(interval);
  }, [job?.id, job?.status, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProjectStatuses = async (projectList = projects) => {
    const ids = (projectList || []).map((project) => project.id).filter(Boolean);
    if (!ids.length) {
      setProjectStatuses({});
      return;
    }
    setLoadingProjectStatuses(true);
    try {
      const data = await apiService.getKnowledgeProjectStatuses(ids);
      const nextStatuses = {};
      (Array.isArray(data) ? data : []).forEach((item) => {
        nextStatuses[String(item.project_id)] = item;
      });
      setProjectStatuses(nextStatuses);
    } catch (err) {
      setError(err.message || "Impossible de charger les statuts RAG des projets.");
    } finally {
      setLoadingProjectStatuses(false);
    }
  };

  const refreshStatus = async (targetProjectId = projectId) => {
    if (!targetProjectId) return;
    setLoadingStatus(true);
    setError("");
    try {
      const data = await apiService.getKnowledgeProjectStatus(targetProjectId);
      setStatus(data);
      if (data?.last_job) setJob(data.last_job);
      setProjectStatuses((current) => ({
        ...current,
        [String(data.project_id)]: summaryFromProjectStatus(data),
      }));
    } catch (err) {
      setError(err.message || "Impossible de charger le statut RAG.");
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleRefresh = () => {
    loadProjectStatuses(projects);
    if (projectId) refreshStatus(projectId);
  };

  const selectProject = (nextProjectId) => {
    setProjectId(nextProjectId ? String(nextProjectId) : "");
    if (!nextProjectId) {
      setProjectSearch("");
      return;
    }
    const project = projects.find((item) => String(item.id) === String(nextProjectId));
    if (project) setProjectSearch(projectLabel(projects, project.id));
  };

  const handleIndex = async () => {
    if (!projectId || !isAdmin) return;
    setIndexing(true);
    setError("");
    try {
      const data = await apiService.indexKnowledgeProject(projectId, {
        include_sources: INDEX_SOURCE_OPTIONS.map((source) => source.value),
        force: true,
      });
      setJob(data);
      setProjectStatuses((current) => ({
        ...current,
        [String(projectId)]: {
          ...(current[String(projectId)] || {}),
          project_id: Number(projectId),
          available: true,
          last_job_status: data.status,
          last_job_message: data.message,
        },
      }));
    } catch (err) {
      setError(err.message || "Impossible de lancer la vectorisation.");
      setIndexing(false);
    }
  };

  const handleDriveSync = async () => {
    if (!projectId) return;
    setSyncingDrive(true);
    setError("");
    setDriveSyncMessage("");
    try {
      const data = await apiService.syncKnowledgeProjectDrive(projectId);
      setDriveSyncMessage(data?.message || "Synchronisation Drive planifiee.");
      refreshStatus(projectId);
      loadProjectStatuses(projects);
    } catch (err) {
      setError(err.message || "Impossible de synchroniser Drive avec Knowledge.");
    } finally {
      setSyncingDrive(false);
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    const cleanQuery = query.trim();
    if (!cleanQuery) return;
    setSearching(true);
    setError("");
    setSelectedSource(null);
    try {
      const data = await apiService.searchKnowledge({
        query: cleanQuery,
        ...(projectId ? { project_id: Number(projectId) } : {}),
        source_types: sourceTypes,
        limit: 16,
      });
      setResults(data.results || []);
    } catch (err) {
      setError(err.message || "Recherche Knowledge impossible.");
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const toggleSource = (value) => {
    setSourceTypes((current) =>
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
    );
  };

  const openSource = async (sourceId) => {
    setLoadingSource(true);
    setError("");
    try {
      const data = await apiService.getKnowledgeSource(sourceId);
      setSelectedSource(data);
    } catch (err) {
      setError(err.message || "Impossible d'ouvrir la source.");
    } finally {
      setLoadingSource(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-5 py-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <Database className="h-3.5 w-3.5" />
            Knowledge RAG
          </div>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">Recherche Knowledge</h1>
        </div>

      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
        <aside className="space-y-4">
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-slate-950">Projets RAG</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {vectorizedProjectCount}/{projects.length} projet(s) vectorise(s)
                </p>
              </div>
              <button
                type="button"
                onClick={handleRefresh}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50"
                title="Rafraichir les statuts RAG"
                aria-label="Rafraichir les statuts RAG"
              >
                <RefreshCw className={`h-4 w-4 ${loadingStatus || loadingProjectStatuses ? "animate-spin" : ""}`} />
              </button>
            </div>
            <div className="relative mt-3">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={projectSearch}
                onChange={(event) => setProjectSearch(event.target.value)}
                placeholder={loadingProjects ? "Chargement des projets..." : "Rechercher un projet, numero ou ID"}
                className="h-10 w-full rounded-md border border-slate-300 bg-white pl-10 pr-3 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                disabled={loadingProjects}
              />
            </div>
            <div className="mt-3 max-h-80 overflow-y-auto rounded-md border border-slate-100">
              <ProjectSelectRow
                active={!projectId}
                label="Tous les projets"
                detail="Recherche dans tout Knowledge"
                onClick={() => selectProject("")}
                summary={null}
                emptyLabel="Global"
              />
              {filteredProjects.slice(0, 80).map((project) => (
                <ProjectSelectRow
                  key={project.id}
                  active={String(project.id) === String(projectId)}
                  label={projectLabel(projects, project.id)}
                  detail={`ID ${project.id}${project.status ? ` - ${project.status}` : ""}`}
                  onClick={() => selectProject(project.id)}
                  summary={projectStatuses[String(project.id)]}
                  loading={loadingProjectStatuses}
                />
              ))}
              {!loadingProjects && filteredProjects.length === 0 && (
                <div className="px-3 py-4 text-sm text-slate-500">Aucun projet ne correspond a cette recherche.</div>
              )}
              {filteredProjects.length > 80 && (
                <div className="border-t border-slate-100 px-3 py-2 text-xs text-slate-500">
                  {filteredProjects.length - 80} autre(s) projet(s), affine la recherche.
                </div>
              )}
            </div>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-slate-950">Index projet</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {selectedProject ? selectedProject.name : "Sélectionne un projet pour voir son index"}
                </p>
              </div>
              {!projectId ? (
                <Database className="h-5 w-5 text-slate-400" />
              ) : !displayStatus?.available ? (
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              ) : isProjectVectorized(displayStatus) ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              ) : (
                <Database className="h-5 w-5 text-slate-400" />
              )}
            </div>
            {projectId && (
              <div className="mt-3">
                <ProjectIndexBadge summary={displayStatus} loading={loadingStatus} />
              </div>
            )}

            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <Metric label="Sources" value={displayStatus?.total_sources ?? 0} />
              <Metric label="Chunks" value={displayStatus?.total_chunks ?? 0} />
              <Metric label="Dernier job" value={job?.status || "-"} />
              <Metric label="Coût estimé" value={`$${Number(job?.cost_estimate_usd || 0).toFixed(4)}`} />
            </dl>

            {job && (
              <div className="mt-4 rounded-md bg-slate-50 p-3 text-sm text-slate-600">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-800">{job.message || job.status}</span>
                  {!STATUS_DONE.has(job.status) && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
                  <div
                    className="h-full bg-blue-600 transition-all"
                    style={{
                      width: `${job.total_sources ? Math.min(100, (job.processed_sources / job.total_sources) * 100) : 0}%`,
                    }}
                  />
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  {job.processed_sources}/{job.total_sources} sources · {job.embedded_chunks} embeddings
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={handleIndex}
              disabled={!projectId || indexing || !isAdmin}
              className="mt-4 inline-flex h-10 w-full items-center justify-center rounded-md bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {indexing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Database className="mr-2 h-4 w-4" />}
              Vectoriser ce projet
            </button>
            <button
              type="button"
              onClick={handleDriveSync}
              disabled={!projectId || syncingDrive}
              className="mt-2 inline-flex h-10 w-full items-center justify-center rounded-md border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${syncingDrive ? "animate-spin" : ""}`} />
              Synchroniser Drive
            </button>
            {driveSyncMessage && <p className="mt-2 text-xs text-slate-500">{driveSyncMessage}</p>}
            {!isAdmin && <p className="mt-2 text-xs text-slate-500">Réservé aux admins pour maîtriser les coûts.</p>}
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-950">
              <Filter className="h-4 w-4" />
              Sources
            </div>
            <div className="space-y-2">
              {SOURCE_OPTIONS.map((source) => (
                <label key={source.value} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm">
                  <span>{source.label}</span>
                  <input
                    type="checkbox"
                    checked={sourceTypes.includes(source.value)}
                    onChange={() => toggleSource(source.value)}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                </label>
              ))}
            </div>
          </section>
        </aside>

        <div className="min-w-0 space-y-4">
          <form onSubmit={handleSearch} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={projectId ? "Recherche sémantique dans ce projet" : "Recherche sémantique dans tout Knowledge"}
                  className="h-11 w-full rounded-md border border-slate-300 bg-white pl-10 pr-3 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>
              <button
                type="submit"
                disabled={searching || !query.trim()}
                className="inline-flex h-11 items-center justify-center rounded-md bg-slate-950 px-4 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {searching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSearch className="mr-2 h-4 w-4" />}
                Chercher
              </button>
            </div>
          </form>

          <section className="space-y-3">
            {results.map((result) => {
              const title = formatKnowledgeText(result.title);
              const excerpt = formatKnowledgeText(result.excerpt);
              return (
              <article key={result.chunk_id} className="min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold uppercase text-slate-600">
                        {sourceLabel(result.source_type)}
                      </span>
                      <span className="text-xs text-slate-500">Score {Math.round(result.score * 1000) / 1000}</span>
                      {!projectId && (
                        <span className="text-xs text-slate-500">
                          {projectLabel(projects, result.project_id)}
                        </span>
                      )}
                    </div>
                    <h3 className="mt-2 break-words text-base font-semibold text-slate-950">{title}</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => openSource(result.source_id)}
                    className="inline-flex shrink-0 items-center text-sm font-medium text-blue-700 hover:text-blue-900"
                  >
                    Ouvrir
                    <ExternalLink className="ml-1 h-4 w-4" />
                  </button>
                </div>
                <p className="mt-3 break-words text-sm leading-6 text-slate-700">{excerpt}</p>
                {result.uri && (
                  <a href={result.uri} className="mt-3 inline-flex text-xs font-medium text-slate-500 hover:text-slate-800">
                    Source liée
                  </a>
                )}
              </article>
              );
            })}
            {!searching && results.length === 0 && (
              <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
                Lance une recherche pour voir les chunks les plus proches.
              </div>
            )}
          </section>
        </div>
      </div>

      {(selectedSource || loadingSource) && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/30 p-4 sm:items-center">
          <div className="max-h-[85vh] w-full max-w-3xl overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <h2 className="min-w-0 break-words pr-4 text-lg font-semibold text-slate-950">
                {loadingSource ? "Chargement..." : formatKnowledgeText(selectedSource?.title)}
              </h2>
              <button
                type="button"
                onClick={() => setSelectedSource(null)}
                className="shrink-0 rounded-md px-2 py-1 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-800"
              >
                Fermer
              </button>
            </div>
            <div className="max-h-[70vh] space-y-3 overflow-y-auto p-5">
              {loadingSource && <Loader2 className="h-5 w-5 animate-spin text-blue-600" />}
              {selectedSource?.chunks?.map((chunk, index) => (
                <div key={chunk.chunk_id} className="overflow-hidden rounded-md border border-slate-200 p-3">
                  <div className="mb-2 text-xs font-semibold uppercase text-slate-500">Chunk {index + 1}</div>
                  <p className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">
                    {formatKnowledgeText(chunk.excerpt)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const WINDOWS_1252_BYTES = {
  0x20ac: 0x80,
  0x201a: 0x82,
  0x0192: 0x83,
  0x201e: 0x84,
  0x2026: 0x85,
  0x2020: 0x86,
  0x2021: 0x87,
  0x02c6: 0x88,
  0x2030: 0x89,
  0x0160: 0x8a,
  0x2039: 0x8b,
  0x0152: 0x8c,
  0x017d: 0x8e,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201c: 0x93,
  0x201d: 0x94,
  0x2022: 0x95,
  0x2013: 0x96,
  0x2014: 0x97,
  0x02dc: 0x98,
  0x2122: 0x99,
  0x0161: 0x9a,
  0x203a: 0x9b,
  0x0153: 0x9c,
  0x017e: 0x9e,
  0x0178: 0x9f,
};

const MOJIBAKE_MARKERS = ["Ã", "Â", "â€", "â€™", "â€œ", "â€�", "â€“", "â€”", "â€¦"];

const formatKnowledgeText = (value) => {
  const text = repairMojibake(String(value || ""));
  return replaceEmbeddedQuillDeltas(text).replace(/\u00a0/g, " ").trim();
};

const repairMojibake = (value) => {
  if (!MOJIBAKE_MARKERS.some((marker) => value.includes(marker))) return value;
  let repaired = value;
  for (let index = 0; index < 2; index += 1) {
    const candidate = decodeMojibakeOnce(repaired);
    if (candidate === repaired || mojibakeScore(candidate) > mojibakeScore(repaired)) break;
    repaired = candidate;
  }
  return repaired;
};

const decodeMojibakeOnce = (value) => {
  const bytes = encodeWindows1252(value);
  if (!bytes) return value;
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return value;
  }
};

const encodeWindows1252 = (value) => {
  const bytes = [];
  for (const char of value) {
    const code = char.charCodeAt(0);
    if (code <= 0xff) {
      bytes.push(code);
    } else if (WINDOWS_1252_BYTES[code]) {
      bytes.push(WINDOWS_1252_BYTES[code]);
    } else {
      return null;
    }
  }
  return new Uint8Array(bytes);
};

const mojibakeScore = (value) =>
  MOJIBAKE_MARKERS.reduce((score, marker) => score + value.split(marker).length - 1, 0);

const replaceEmbeddedQuillDeltas = (value) => {
  if (!value.includes('"ops"') && !value.includes('"insert"')) return value;
  let output = "";
  let index = 0;
  while (index < value.length) {
    const starts = [];
    for (const needle of ['{"ops"', '[{"insert"']) {
      const position = value.indexOf(needle, index);
      if (position >= 0) starts.push(position);
    }
    if (!starts.length) {
      output += value.slice(index);
      break;
    }
    const start = Math.min(...starts);
    const end = findJsonEnd(value, start);
    if (end < 0) {
      output += value.slice(index);
      break;
    }
    output += value.slice(index, start);
    const rawJson = value.slice(start, end);
    const deltaText = quillDeltaToText(rawJson);
    output += deltaText || rawJson;
    index = end;
  }
  return output;
};

const findJsonEnd = (value, start) => {
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let index = start; index < value.length; index += 1) {
    const char = value[index];
    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (char === "\\") {
        escaped = true;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }
    if (char === '"') inString = true;
    if (char === "{" || char === "[") depth += 1;
    if (char === "}" || char === "]") depth -= 1;
    if (depth === 0) return index + 1;
  }
  return -1;
};

const quillDeltaToText = (value) => {
  let payload = value;
  if (typeof value === "string") {
    try {
      payload = JSON.parse(value);
    } catch {
      return "";
    }
  }
  const ops = Array.isArray(payload?.ops) ? payload.ops : Array.isArray(payload) ? payload : [];
  if (!ops.length) return "";
  return repairMojibake(
    ops
      .map((op) => {
        if (typeof op?.insert === "string") return op.insert;
        if (op?.insert?.["drive-ref"]) return op.insert["drive-ref"].name || "Référence Drive";
        if (op?.insert?.["stock-tag"]) {
          const tag = op.insert["stock-tag"];
          return tag.item_name || tag.itemName || tag.serial_number || tag.serialNumber || "Stock";
        }
        if (op?.insert?.["task-card"]) return op.insert["task-card"].name || "Tâche";
        return "";
      })
      .join("")
  ).trim();
};

const Metric = ({ label, value }) => (
  <div className="border-l border-slate-200 pl-3">
    <dt className="text-xs font-medium uppercase text-slate-500">{label}</dt>
    <dd className="mt-1 text-base font-semibold text-slate-950">{value}</dd>
  </div>
);

const ProjectSelectRow = ({ active, label, detail, onClick, summary, loading = false, emptyLabel = "Non verifie" }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex w-full items-center justify-between gap-3 border-b border-slate-100 px-3 py-2 text-left text-sm transition last:border-b-0 ${
      active ? "bg-blue-50 text-blue-950" : "bg-white text-slate-800 hover:bg-slate-50"
    }`}
  >
    <span className="min-w-0">
      <span className="block truncate font-medium">{label}</span>
      <span className="mt-0.5 block truncate text-xs text-slate-500">{detail}</span>
    </span>
    <ProjectIndexBadge summary={summary} loading={loading} compact emptyLabel={emptyLabel} />
  </button>
);

const ProjectIndexBadge = ({ summary, loading = false, compact = false, emptyLabel = "Non verifie" }) => {
  const baseClass = compact
    ? "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold"
    : "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold";
  if (loading) {
    return <span className={`${baseClass} bg-slate-100 text-slate-500`}>Statut...</span>;
  }
  if (!summary) {
    return <span className={`${baseClass} bg-slate-100 text-slate-500`}>{emptyLabel}</span>;
  }
  if (summary.available === false) {
    return <span className={`${baseClass} bg-amber-50 text-amber-700`}>RAG indispo</span>;
  }
  if (["queued", "running"].includes(summary.last_job_status)) {
    return <span className={`${baseClass} bg-blue-50 text-blue-700`}>En cours</span>;
  }
  if (isProjectVectorized(summary)) {
    const chunks = Number(summary.total_chunks || 0);
    return <span className={`${baseClass} bg-emerald-50 text-emerald-700`}>{chunks} chunks</span>;
  }
  return <span className={`${baseClass} bg-slate-100 text-slate-600`}>Non vectorise</span>;
};

const summaryFromProjectStatus = (status) => ({
  project_id: status.project_id,
  available: status.available,
  message: status.message,
  vectorized: Number(status.total_chunks || 0) > 0,
  sources_by_type: status.sources_by_type || {},
  chunks_by_type: status.chunks_by_type || {},
  total_sources: status.total_sources || 0,
  total_chunks: status.total_chunks || 0,
  last_job_status: status.last_job?.status || null,
  last_job_message: status.last_job?.message || null,
  last_indexed_at: status.last_indexed_at || null,
});

const isProjectVectorized = (summary) => Boolean(summary?.vectorized || Number(summary?.total_chunks || 0) > 0);

const normalizeProjectSearch = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const sourceLabel = (value) => {
  const match = SOURCE_OPTIONS.find((source) => source.value === value);
  return match?.label || value;
};

const projectLabel = (projects, projectId) => {
  const project = projects.find((item) => String(item.id) === String(projectId));
  if (!project) return `Projet #${projectId}`;
  return project.project_number ? `${project.project_number} - ${project.name}` : project.name;
};

export default KnowledgePage;
